import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityEventsAPI, eventsAPI, commentsAPI } from './services/api';
import CommunityEventCard from './CommunityEventCard';
import PastEventCard from './PastEventCard';
import OrganizeEventDialog from './OrganizeEventDialog';
import UserDetail from './UserDetail';
import { isEventPast } from './utils/dateUtils';
import './EventDetailPage.css';

function EventDetailPage({ currentUser, onOpenLogin }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followedEvents, setFollowedEvents] = useState(new Set());
  const [showGatheringDialog, setShowGatheringDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => { loadEvent(); }, [id]);

  useEffect(() => {
    if (!currentUser) return;
    communityEventsAPI.getUserFollowed(currentUser.id)
      .then(followed => setFollowedEvents(new Set(followed.map(e => e.id))))
      .catch(() => {});
  }, [currentUser]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const data = await communityEventsAPI.getById(id);
      setEvent(data);
    } catch {
      setError(t('eventDetailPage.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async (eventId) => {
    if (!currentUser) { onOpenLogin?.(); return; }
    const isFollowing = followedEvents.has(eventId);
    try {
      if (isFollowing) {
        await communityEventsAPI.unfollow(eventId);
        setFollowedEvents(prev => { const s = new Set(prev); s.delete(eventId); return s; });
        setEvent(prev => ({ ...prev, followCount: prev.followCount - 1 }));
      } else {
        await communityEventsAPI.follow(eventId);
        setFollowedEvents(prev => new Set(prev).add(eventId));
        setEvent(prev => ({ ...prev, followCount: prev.followCount + 1 }));
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    }
  };

  const handleVote = async (eventId, voteValue) => {
    if (!currentUser) { onOpenLogin?.(); return; }
    try {
      await communityEventsAPI.vote(eventId, voteValue);
      await loadEvent();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  const handleJoinEvent = async (eventId) => {
    if (!currentUser) { onOpenLogin?.(); return; }
    try {
      await eventsAPI.joinEvent(eventId);
      await loadEvent();
    } catch (err) {
      console.error('Error joining event:', err);
      await loadEvent();
    }
  };

  const handleCommentVote = async (commentId, voteValue) => {
    if (!currentUser) { onOpenLogin?.(); return; }
    try {
      await commentsAPI.vote(commentId, voteValue);
      await loadEvent();
    } catch (err) {
      console.error('Error voting on comment:', err);
    }
  };

  const handleCreateGathering = (communityEvent) => {
    if (!currentUser) { onOpenLogin?.(); return; }
    setSelectedEvent(communityEvent);
    setShowGatheringDialog(true);
  };

  const handleCloseGatheringDialog = async (gatheringCreated = false) => {
    setShowGatheringDialog(false);
    setSelectedEvent(null);
    if (gatheringCreated) await loadEvent();
  };

  if (loading) {
    return (
      <div className="event-detail-page">
        <p className="event-detail-status">{t('eventDetailPage.loading')}</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-page">
        <p className="event-detail-status">{error || t('eventDetailPage.notFound')}</p>
        <a href="/" className="event-detail-home-link">{t('eventDetailPage.goToMain')}</a>
      </div>
    );
  }

  return (
    <div className="event-detail-page">
      {!currentUser && (
        <div className="event-detail-login-prompt">
          <span>{t('eventDetailPage.loginPrompt')}</span>
          <a className="login-cta-btn" href="/">{t('eventDetailPage.loginRegisterButton')}</a>
        </div>
      )}
      <div className="event-detail-card-wrap">
        {(() => {
          const gatheringEnded = !event.autoCreatedEventId && !!event.anyGatheringId;
          const gatheringDateTime = event.autoCreatedEventId ? event.autoCreatedEventDateTime : (event.anyGatheringDateTime || event.dateTime);
          const isPast = event.status === 1 && event.hasGatherings && isEventPast(gatheringDateTime, gatheringEnded);
          return isPast ? (
            <PastEventCard
              event={event}
              currentUser={currentUser}
              onOpenGallery={null}
              onOrganizerClick={setSelectedUserId}
              hideEventLink={true}
            />
          ) : (
            <CommunityEventCard
              event={event}
              currentUser={currentUser}
              showVoting={true}
              followedEvents={followedEvents}
              onFollowToggle={handleFollowToggle}
              onVote={handleVote}
              onJoinEvent={handleJoinEvent}
              onCreateGathering={handleCreateGathering}
              onCommentVote={handleCommentVote}
              onOpenGallery={null}
              onOrganizerClick={setSelectedUserId}
            />
          );
        })()}
      </div>

      <OrganizeEventDialog
        isOpen={showGatheringDialog}
        onClose={handleCloseGatheringDialog}
        communityEvent={selectedEvent}
        currentUser={currentUser}
      />

      {selectedUserId && (
        <UserDetail
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
}

export default EventDetailPage;
